/**
 * Night-window math + window-usage approximation (phase 9 §B/§B2).
 *
 * The plane enforces the budget; this module only RENDERS it — computing
 * the current (or most recent) night window in the tenant's timezone and
 * summing session tokens inside it for the Tonight budget bar. Kept as a
 * small focused module: phase 12 lifts daemon-side budget/seat logic into
 * a RuntimeAdapter, and the web's window math should stay a clean seam.
 */

import type { Runtime, Session } from "./api";

/** Plane defaults (contract §B). */
export const DEFAULT_WINDOW_START = "22:00";
export const DEFAULT_WINDOW_END = "08:00";
export const DEFAULT_TIMEZONE = "America/New_York";

export interface NightWindow {
  /** Epoch ms of the most recent window start at/before "now". */
  start: number;
  /** Epoch ms when that window ends (start + duration). */
  end: number;
  /** Whether "now" falls inside [start, end). */
  active: boolean;
}

function parseHHMM(s: string | undefined, fallback: string): {
  h: number;
  m: number;
} {
  const m = /^(\d{1,2}):(\d{2})$/.exec((s ?? "").trim());
  const [h, min] = m
    ? [Number(m[1]), Number(m[2])]
    : fallback.split(":").map(Number);
  return { h: Math.min(23, Math.max(0, h)), m: Math.min(59, Math.max(0, min)) };
}

function tzParts(tz: string, at: number): {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
} {
  const f = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const p: Record<string, string> = {};
  for (const part of f.formatToParts(new Date(at))) p[part.type] = part.value;
  return {
    y: Number(p.year),
    mo: Number(p.month),
    d: Number(p.day),
    h: Number(p.hour),
    mi: Number(p.minute),
  };
}

/**
 * Epoch ms of a wall-clock time in a timezone. Two-pass correction: guess
 * the instant as if the wall time were UTC, see what wall time that
 * renders in the zone, shift by the difference (second pass settles DST
 * edges; nonexistent spring-forward times land on the shifted clock).
 */
function wallTimeToEpoch(
  tz: string,
  y: number,
  mo: number,
  d: number,
  h: number,
  mi: number
): number {
  const want = Date.UTC(y, mo - 1, d, h, mi);
  let ts = want;
  for (let i = 0; i < 2; i++) {
    const p = tzParts(tz, ts);
    const shown = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi);
    ts += want - shown;
  }
  return ts;
}

/**
 * The current night window: the most recent occurrence of the start time
 * (today or yesterday in the tenant tz) plus the start→end duration. The
 * window may span midnight (22:00–08:00 default); equal start/end reads
 * as a full 24h window.
 */
export function currentNightWindow(
  startHHMM: string | undefined,
  endHHMM: string | undefined,
  tz: string | undefined,
  now = Date.now()
): NightWindow {
  const zone = tz?.trim() || DEFAULT_TIMEZONE;
  const start = parseHHMM(startHHMM, DEFAULT_WINDOW_START);
  const end = parseHHMM(endHHMM, DEFAULT_WINDOW_END);
  const durationMin =
    ((end.h * 60 + end.m - (start.h * 60 + start.m)) % 1440 + 1440) % 1440 ||
    1440;

  let today: ReturnType<typeof tzParts>;
  let startTs: number;
  try {
    today = tzParts(zone, now);
    startTs = wallTimeToEpoch(zone, today.y, today.mo, today.d, start.h, start.m);
    if (startTs > now) {
      // The window started yesterday (tz-local). Date.UTC rollover inside
      // wallTimeToEpoch handles d-1 = 0 (previous month) correctly.
      startTs = wallTimeToEpoch(
        zone,
        today.y,
        today.mo,
        today.d - 1,
        start.h,
        start.m
      );
    }
  } catch {
    // Unknown timezone string — fall back to the browser's local clock.
    const local = new Date(now);
    startTs = new Date(
      local.getFullYear(),
      local.getMonth(),
      local.getDate(),
      start.h,
      start.m
    ).getTime();
    if (startTs > now) startTs -= 24 * 3600 * 1000;
  }

  const endTs = startTs + durationMin * 60 * 1000;
  return { start: startTs, end: endTs, active: now >= startTs && now < endTs };
}

/**
 * APPROXIMATE window usage from the session rows the dashboard already
 * polls, used when the plane doesn't return a §B2 rollup on GET /settings:
 * whole-session input+output totals (cache excluded, matching the §B cap
 * semantics) attributed to the window their session STARTED in. Sessions
 * spanning the window edge aren't sliced, and the dashboard only holds the
 * newest ~30 rows — so this is a lower-bound estimate, labeled as such in
 * the UI.
 */
export function windowSessionTokens(
  sessions: Session[],
  win: NightWindow
): { tokens: number; sessions: number; byRuntime: Partial<Record<Runtime, number>> } {
  let tokens = 0;
  let count = 0;
  // Phase 12: split the same per-session totals by the runtime each ran on
  // (absent ⇒ "claude"), so the budget card can show a Claude/Codex split
  // even when the plane returns no §B2 rollup.
  const byRuntime: Partial<Record<Runtime, number>> = {};
  for (const s of sessions) {
    const t = Date.parse(s.startedAt ?? "");
    if (!Number.isFinite(t) || t < win.start || t >= win.end) continue;
    const used = (s.inputTokens ?? 0) + (s.outputTokens ?? 0);
    tokens += used;
    count++;
    const rt: Runtime = s.runtime ?? "claude";
    byRuntime[rt] = (byRuntime[rt] ?? 0) + used;
  }
  return { tokens, sessions: count, byRuntime };
}

/** "10:00 PM–8:00 AM" — window readback for the budget card. */
export function windowLabel(
  startHHMM: string | undefined,
  endHHMM: string | undefined
): string {
  const fmt = ({ h, m }: { h: number; m: number }) => {
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
  };
  return `${fmt(parseHHMM(startHHMM, DEFAULT_WINDOW_START))}–${fmt(
    parseHHMM(endHHMM, DEFAULT_WINDOW_END)
  )}`;
}
